import { supabase } from "@/lib/supabaseClient";

export interface GrammarLesson {
  id?: string;
  order_index: number;
  level: string; // 'A1' | 'A2' | 'B1' | 'B2'
  title: string;
  slug: string;
  description: string | null;
  category: string | null;
  is_published: boolean;
  is_locked_by_default: boolean;
  dialogue?: {
    title: string;
    context: string;
    characters: string[];
    lines: { speaker: string; text: string }[];
    vocabularyNotes: { term: string; meaning: string }[];
  } | null;
  content: {
    intro: string;
    rule: string;
    structure?: string;
    examples: (string | { sentence: string; translation?: string; note?: string })[];
    commonMistakes: { wrong?: string; incorrect?: string; correct: string; explanation: string }[];
    tefTip: string;
  };
  exercises: (
    | {
        type: "multiple_choice";
        prompt: string;
        options: string[];
        answer: string;
        explanation: string;
      }
    | {
        type: "fill_blank";
        prompt: string;
        answer: string;
        explanation: string;
      }
    | {
        type: "transformation";
        prompt: string;
        answer: string;
        explanation: string;
      }
  )[];
  flashcards?: {
    front: string;
    back: string;
    category?: string;
    difficulty?: "easy" | "medium" | "hard";
  }[] | null;
  estimated_minutes?: number;
  created_at?: string;
  updated_at?: string;
}

export function normalizeLesson(lesson: GrammarLesson): GrammarLesson {
  if (!lesson) return lesson;

  const content = lesson.content || { intro: "", rule: "", structure: "", examples: [], commonMistakes: [], tefTip: "" };
  
  // 1. Normalize examples from strings or objects
  let examples: { sentence: string; translation: string; note: string }[] = [];
  if (Array.isArray(content.examples)) {
    examples = content.examples.map(ex => {
      if (typeof ex === "string") {
        return { sentence: ex, translation: "", note: "" };
      }
      return {
        sentence: ex?.sentence || "",
        translation: ex?.translation || "",
        note: ex?.note || ""
      };
    });
  }

  // 2. Normalize common mistakes
  let commonMistakes: { wrong: string; correct: string; explanation: string }[] = [];
  if (Array.isArray(content.commonMistakes)) {
    commonMistakes = content.commonMistakes.map(cm => {
      return {
        wrong: cm?.wrong || cm?.incorrect || "",
        correct: cm?.correct || "",
        explanation: cm?.explanation || ""
      };
    });
  }

  // 3. Normalize dialogue
  let dialogue = lesson.dialogue;
  if (!dialogue || typeof dialogue !== "object") {
    dialogue = null;
  } else {
    dialogue = {
      title: dialogue.title || "",
      context: dialogue.context || "",
      characters: Array.isArray(dialogue.characters) ? dialogue.characters : [],
      lines: Array.isArray(dialogue.lines) ? dialogue.lines.map(line => ({
        speaker: line?.speaker || "",
        text: line?.text || ""
      })) : [],
      vocabularyNotes: Array.isArray(dialogue.vocabularyNotes) ? dialogue.vocabularyNotes.map(vn => ({
        term: vn?.term || "",
        meaning: vn?.meaning || ""
      })) : []
    };
  }

  // 4. Normalize exercises (supporting legacy MCQ checks)
  const exercises = Array.isArray(lesson.exercises) ? lesson.exercises.map(ex => {
    if (ex.type === "multiple_choice") {
      return {
        type: "multiple_choice" as const,
        prompt: ex.prompt || "",
        options: Array.isArray(ex.options) ? ex.options : [],
        answer: ex.answer || "",
        explanation: ex.explanation || ""
      };
    } else if (ex.type === "fill_blank") {
      return {
        type: "fill_blank" as const,
        prompt: ex.prompt || "",
        answer: ex.answer || "",
        explanation: ex.explanation || ""
      };
    } else {
      return {
        type: "transformation" as const,
        prompt: ex.prompt || "",
        answer: ex.answer || "",
        explanation: ex.explanation || ""
      };
    }
  }) : [];

  // 5. Normalize flashcards
  const flashcards = Array.isArray(lesson.flashcards) ? lesson.flashcards.map(fc => ({
    front: fc?.front || "",
    back: fc?.back || "",
    category: fc?.category || "",
    difficulty: fc?.difficulty || "easy"
  })) : [];

  return {
    ...lesson,
    dialogue,
    content: {
      intro: content.intro || "",
      rule: content.rule || "",
      structure: content.structure || "",
      examples,
      commonMistakes,
      tefTip: content.tefTip || ""
    },
    exercises,
    flashcards,
    estimated_minutes: lesson.estimated_minutes || 10
  };
}


export interface GrammarProgress {
  id?: string;
  user_id?: string;
  lesson_id: string;
  status: "not_started" | "in_progress" | "completed";
  score: number;
  answers?: any;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

class GrammarService {
  /**
   * Fetch all lessons.
   * Regular users will only see published lessons due to DB RLS policies.
   * Admins will see all lessons (drafts and published).
   */
  async fetchLessons(): Promise<GrammarLesson[]> {
    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from("grammar_lessons")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) {
        console.error("[GrammarService] Error fetching lessons:", error);
        return [];
      }
      return (data || []).map(normalizeLesson);
    } catch (err) {
      console.error("[GrammarService] Exception fetching lessons:", err);
      return [];
    }
  }

  /**
   * Fetch a single lesson by its unique slug.
   */
  async getLessonBySlug(slug: string): Promise<GrammarLesson | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from("grammar_lessons")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error) {
        console.error("[GrammarService] Error fetching lesson by slug:", error);
        return null;
      }
      return data ? normalizeLesson(data) : null;
    } catch (err) {
      console.error("[GrammarService] Exception fetching lesson by slug:", err);
      return null;
    }
  }

  /**
   * Create a new lesson (admin only).
   */
  async createLesson(lesson: Omit<GrammarLesson, "id" | "created_at" | "updated_at">): Promise<GrammarLesson | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from("grammar_lessons")
        .insert(lesson)
        .select()
        .single();

      if (error) {
        console.error("[GrammarService] Error creating lesson:", error);
        throw error;
      }
      return data ? normalizeLesson(data) : null;
    } catch (err) {
      console.error("[GrammarService] Exception creating lesson:", err);
      return null;
    }
  }

  /**
   * Update an existing lesson (admin only).
   */
  async updateLesson(id: string, lesson: Partial<GrammarLesson>): Promise<GrammarLesson | null> {
    if (!supabase) return null;
    try {
      // Exclude read-only fields
      const { id: _, created_at: __, updated_at: ___, ...payload } = lesson as any;
      const { data, error } = await supabase
        .from("grammar_lessons")
        .update(payload)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("[GrammarService] Error updating lesson:", error);
        throw error;
      }
      return data ? normalizeLesson(data) : null;
    } catch (err) {
      console.error("[GrammarService] Exception updating lesson:", err);
      return null;
    }
  }

  /**
   * Delete a lesson (admin only).
   */
  async deleteLesson(id: string): Promise<boolean> {
    if (!supabase) return false;
    try {
      const { error } = await supabase
        .from("grammar_lessons")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("[GrammarService] Error deleting lesson:", error);
        return false;
      }
      return true;
    } catch (err) {
      console.error("[GrammarService] Exception deleting lesson:", err);
      return false;
    }
  }

  /**
   * Bulk delete grammar lessons by their IDs (admin only).
   */
  async deleteLessons(ids: string[]): Promise<boolean> {
    if (!supabase || ids.length === 0) return false;
    try {
      const { error } = await supabase
        .from("grammar_lessons")
        .delete()
        .in("id", ids);

      if (error) {
        console.error("[GrammarService] Error deleting lessons:", error);
        return false;
      }
      return true;
    } catch (err) {
      console.error("[GrammarService] Exception deleting lessons:", err);
      return false;
    }
  }

  /**
   * Bulk import grammar lessons (admin only).
   */
  async bulkImportLessons(
    lessons: Omit<GrammarLesson, "id" | "created_at" | "updated_at">[],
    duplicateStrategy: "ignore" | "overwrite"
  ): Promise<{ success: boolean; count: number; error?: string }> {
    if (!supabase) return { success: false, count: 0, error: "Supabase client not initialized" };
    try {
      let finalLessons = [...lessons];

      if (duplicateStrategy === "ignore") {
        // Fetch existing slugs
        const { data: existing, error: fetchError } = await supabase
          .from("grammar_lessons")
          .select("slug");

        if (fetchError) {
          console.error("[GrammarService] Error fetching existing slugs for import:", fetchError);
          return { success: false, count: 0, error: fetchError.message };
        }

        const existingSlugs = new Set((existing || []).map((row) => row.slug));
        finalLessons = finalLessons.filter((l) => !existingSlugs.has(l.slug));
      }

      if (finalLessons.length === 0) {
        return { success: true, count: 0 };
      }

      const { error } = await supabase
        .from("grammar_lessons")
        .upsert(finalLessons, { onConflict: "slug" });

      if (error) {
        console.error("[GrammarService] Error in bulk upsert:", error);
        return { success: false, count: 0, error: error.message };
      }

      return { success: true, count: finalLessons.length };
    } catch (err: any) {
      console.error("[GrammarService] Exception during bulk import:", err);
      return { success: false, count: 0, error: err.message || String(err) };
    }
  }


  /**
   * Fetch all progress records for the currently authenticated user.
   */
  async fetchUserProgress(): Promise<GrammarProgress[]> {
    if (!supabase) return [];
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) return [];

      const { data, error } = await supabase
        .from("grammar_progress")
        .select("*")
        .eq("user_id", userData.user.id);

      if (error) {
        console.error("[GrammarService] Error fetching progress:", error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error("[GrammarService] Exception fetching progress:", err);
      return [];
    }
  }

  /**
   * Save / Upsert user progress for a specific lesson.
   */
  async updateUserProgress(
    lessonId: string,
    status: "not_started" | "in_progress" | "completed",
    score: number,
    answers?: any
  ): Promise<GrammarProgress | null> {
    if (!supabase) return null;
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        console.warn("[GrammarService] Unauthenticated progress update attempt.");
        return null;
      }

      // Check for existing progress first to avoid unique constraint errors on upsert
      const { data: existingProgress, error: fetchError } = await supabase
        .from("grammar_progress")
        .select("id")
        .eq("user_id", userData.user.id)
        .eq("lesson_id", lessonId)
        .maybeSingle();

      if (fetchError) {
        console.error("[GrammarService] Error fetching existing progress:", fetchError);
        return null;
      }

      const payload: any = {
        user_id: userData.user.id,
        lesson_id: lessonId,
        status,
        score,
        completed_at: status === "completed" ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      };

      if (answers !== undefined) {
        payload.answers = answers;
      }

      let queryResult;
      if (existingProgress?.id) {
        queryResult = await supabase
          .from("grammar_progress")
          .update(payload)
          .eq("id", existingProgress.id)
          .select()
          .single();
      } else {
        queryResult = await supabase
          .from("grammar_progress")
          .insert(payload)
          .select()
          .single();
      }

      if (queryResult.error) {
        console.error("[GrammarService] Error updating progress:", queryResult.error);
        return null;
      }
      return queryResult.data;
    } catch (err) {
      console.error("[GrammarService] Exception updating progress:", err);
      return null;
    }
  }
}

export const grammarService = new GrammarService();
